// Role hierarchy + management helpers — mirrors backend `permissions_catalog.py`.
export const ROLE_RANK = {
  super_admin: 7,
  admin: 6,
  manager: 5,
  team_leader: 4,
  l2_agent: 3,
  l1_agent: 2,
  agent: 2,
  client: 1,
};

export const ROLE_LABELS = {
  super_admin: "Super Admin",
  admin: "Admin",
  manager: "Manager",
  team_leader: "Team Leader",
  l2_agent: "L2 Agent",
  l1_agent: "L1 Agent",
  agent: "Agent",
  client: "Client",
  // legacy fallbacks
  supervisor: "Manager",
  viewer: "Client",
};

/**
 * Roles that a caller may assign (invite or promote to). Strict hierarchy:
 * always strictly below the caller's own rank. Super admin is never
 * assignable via the UI. Pass `clientRoles` (the active client's role list
 * from /clients) to restrict to the client-specific hierarchy.
 */
export function assignableRoles(caller, clientRoles) {
  if (!caller) return [];
  let base;
  if (caller.is_super_admin) {
    base = Object.keys(ROLE_RANK).filter((r) => r !== "super_admin");
  } else {
    const myRank = ROLE_RANK[caller.role] || 0;
    base = Object.entries(ROLE_RANK)
      .filter(([r, rank]) => rank < myRank && r !== "super_admin")
      .map(([r]) => r);
  }
  if (Array.isArray(clientRoles) && clientRoles.length > 0) {
    base = base.filter((r) => clientRoles.includes(r));
  }
  return base;
}

/**
 * Lowest operational role for the active client — the sensible invite default
 * ("agent" for lead-management clients, "l1_agent" for ticketing).
 */
export function defaultRoleFor(clientRoles) {
  if (Array.isArray(clientRoles) && clientRoles.length > 0) {
    if (clientRoles.includes("l1_agent")) return "l1_agent";
    if (clientRoles.includes("agent")) return "agent";
    const operational = clientRoles.filter((r) => r !== "super_admin" && r !== "client");
    if (operational.length > 0) {
      return operational.reduce((lo, r) =>
        (ROLE_RANK[r] || 0) < (ROLE_RANK[lo] || 0) ? r : lo);
    }
  }
  return "l1_agent";
}

/**
 * True if `caller` may invite/edit/deactivate `target`.
 * - Super admins manage anyone except other super admins via the UI.
 * - Strict hierarchy: must be above target's rank.
 * - Super-admin targets are never manageable via the API.
 */
export function canManageTarget(caller, target) {
  if (!caller || !target) return false;
  if (target.is_super_admin) return false;
  if (caller.is_super_admin) return true;
  const cr = ROLE_RANK[caller.role] || 0;
  const tr = ROLE_RANK[target.role] || 0;
  return cr > tr;
}

/**
 * Legacy permission-key aliases → new granular keys. Mirrors backend
 * `permissions_catalog.LEGACY_PERMISSION_ALIASES`. Lets older `hasPerm("…")`
 * call-sites keep working against the new effective-permission set.
 */
export const LEGACY_PERMISSION_ALIASES = {
  create_ticket: "tickets.create",
  view_all_tickets: "tickets.view_all",
  comment_ticket: "tickets.add_comment",
  send_reminder: "tickets.send_reminder",
  change_status: "tickets.change_status",
  reopen_ticket: "tickets.reopen",
  close_ticket: "tickets.close",
  cancel_ticket: "tickets.cancel",
  delete_ticket: "tickets.delete",
  edit_lookups: "catalogue.manage_issue_types",
  edit_templates: "email_templates.edit",
  edit_wa_templates: "wa_templates.manage",
  edit_automation: "automation.edit",
  edit_permissions: "users.manage_permissions",
  edit_field_reqs: "field_rules.edit",
  manage_users: "users.create",
  delete_user_permanent: "users.delete",
  manage_pocs: "contacts.edit",
  view_dashboard: "dashboard.view",
  view_audit_log: "logs.view_change",
  view_reports: "reports.view",
  export_data: "reports.export_excel",
};

export function resolvePermKey(action) {
  return LEGACY_PERMISSION_ALIASES[action] || action;
}
